// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

export type SinkKind = 'fsa' | 'blob' | 'none';

/** Blob ceilings are folklore (Chrome ~2 GB, Firefox ~800 MB). 512 MiB is the conservative budget. */
export const BLOB_CAP_BYTES = 512 * 1024 * 1024;

export interface Sink {
  readonly kind: SinkKind;
  write(bytes: Uint8Array): Promise<void>;
  /** Returns a Blob for the in-memory branch, or null when the bytes went straight to disk. */
  close(): Promise<Blob | null>;
  abort(): Promise<void>;
  readonly written: number;
}

interface SaveFilePickerOptions {
  suggestedName?: string;
  types?: { description: string; accept: Record<string, string[]> }[];
}
type ShowSaveFilePicker = (o?: SaveFilePickerOptions) => Promise<FileSystemFileHandle>;

export function hasFileSystemAccess(): boolean {
  return typeof (globalThis as { showSaveFilePicker?: ShowSaveFilePicker }).showSaveFilePicker === 'function';
}

/**
 * Which sink this browser can actually use.
 *
 * The common framing — "no phone can stream a file" — is wrong. The split is Chromium against
 * WebKit and Gecko: `showSaveFilePicker` is in Chrome 86 desktop AND Chrome Android 132, and in
 * no version of Safari or Firefox.
 */
export function describeSink(kind: SinkKind, estimatedBytes: number): string {
  switch (kind) {
    case 'fsa':
      return 'This browser can stream the cleaned file straight to disk, so its size is limited only by your free space.';
    case 'blob':
      return `This browser can only build the cleaned file in memory, and scrubjay caps that at 512 MB. ${
        estimatedBytes > BLOB_CAP_BYTES
          ? 'Your file is bigger than that, so the cleaned copy is not available here — findings and the encrypted mapping still are.'
          : 'Your file fits.'
      }`;
    case 'none':
      return 'Only findings and the encrypted mapping are produced on this run.';
  }
}

export function chooseSink(estimatedBytes: number, wantCleanFile: boolean): SinkKind {
  if (!wantCleanFile) return 'none';
  if (hasFileSystemAccess()) return 'fsa';
  if (estimatedBytes <= BLOB_CAP_BYTES) return 'blob';
  return 'none';
}

/**
 * Open the chosen sink.
 *
 * `showSaveFilePicker` MUST be called synchronously inside the click gesture — nothing awaited
 * before it — or Safari-style gesture rules and Chrome's own user-activation check reject it.
 *
 * `mode: 'exclusive'` is deliberately NOT used: it is Chrome 121+ only, and it writes in place,
 * so a tab crash leaves a truncated file that looks complete. For a file whose entire purpose is
 * to be uploaded somewhere, that is the worst possible failure. The default swap-file plus
 * atomic replace needs about 2x the output in transient free space, which preflight states.
 */
export async function openSink(kind: SinkKind, suggestedName: string): Promise<Sink> {
  if (kind === 'fsa') {
    const picker = (globalThis as { showSaveFilePicker?: ShowSaveFilePicker }).showSaveFilePicker!;
    const handle = await picker({
      suggestedName,
      types: [
        { description: 'JSON Lines', accept: { 'application/x-ndjson': ['.jsonl'] } },
        { description: 'Text', accept: { 'text/plain': ['.txt', '.csv'] } },
      ],
    });
    const stream = await handle.createWritable({ keepExistingData: false });
    let written = 0;
    return {
      kind,
      get written() {
        return written;
      },
      async write(bytes) {
        // The awaited promise IS the backpressure. Do not fire and forget.
        await stream.write(bytes as BufferSource);
        written += bytes.length;
      },
      async close() {
        await stream.close();
        return null;
      },
      async abort() {
        try {
          await stream.abort();
        } catch {
          /* the stream may already be closed */
        }
      },
    };
  }

  if (kind === 'blob') {
    const parts: Uint8Array[] = [];
    let written = 0;
    return {
      kind,
      get written() {
        return written;
      },
      async write(bytes) {
        if (written + bytes.length > BLOB_CAP_BYTES) {
          throw new Error(
            'The cleaned file passed 512 MB, which is scrubjay’s in-memory limit in this browser. Your findings and mapping are unaffected. Use a Chromium browser to stream a file this size straight to disk.',
          );
        }
        parts.push(bytes.slice());
        written += bytes.length;
      },
      async close() {
        return new Blob(parts as BlobPart[], { type: 'application/x-ndjson' });
      },
      async abort() {
        parts.length = 0;
      },
    };
  }

  let written = 0;
  return {
    kind: 'none',
    get written() {
      return written;
    },
    async write(bytes) {
      written += bytes.length;
    },
    async close() {
      return null;
    },
    async abort() {
      /* nothing to undo */
    },
  };
}
